"use client";

import {
  IDedicatedResourceRef,
  WorkplaceRequestReleasedResourceAction
} from "@gettactic/api";
import { UnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { cn } from "@gettactic/helpers";
import React from "react";
import { classes } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/utils";
import {
  type DeskAutomationStep,
  RELEASED_DESK_ACTION_LABEL,
  readReleasedResourceAction,
  type VacatedDesk
} from "@/features/workplace-requests/lib/desk-automation";

/** The three choices, in the order they escalate. */
const OPTIONS = (["none", "make_open", "make_unavailable"] as const).map(
  (value) => ({ id: value, name: RELEASED_DESK_ACTION_LABEL[value] })
);

export interface ReleasedDeskChoiceListProps {
  /** The desk moves this approval will run, already filtered to the enabled ones. */
  steps: DeskAutomationStep[];
  /** Names a desk the step points at, when it is known. */
  deskName: (resourceId: string | null) => string | null;
  /** The desk each move frees, as far as it could be looked up on the person. */
  vacatedDesk: (step: DeskAutomationStep) => VacatedDesk<IDedicatedResourceRef>;
  /** Approver picks, by workflow id. A missing entry means the rule's own choice. */
  overrides: Record<string, WorkplaceRequestReleasedResourceAction>;
  onChange: (
    workflowId: string,
    action: WorkplaceRequestReleasedResourceAction
  ) => void;
  /** Freezes the selects while the approval is in flight. */
  isDisabled?: boolean;
}

/**
 * What becomes of the desk each move frees, editable for this one approval.
 *
 * The request type's rule supplies the answer for the normal case; this is
 * where the approver disagrees with it, because whether a vacated desk should
 * go back in the pool or be held empty depends on facts the rule can't know —
 * the person is being replaced next week, the floor is being rebuilt.
 *
 * It sits inside the approval box alongside the follow-up checkboxes rather
 * than behind the Approve click: it is part of the decision, not a confirmation
 * of one already made.
 *
 * A move known to free nothing — the person already sits at the destination,
 * or holds no desk yet — gets no row: a choice with no effect is not a
 * decision, and the automation panel below says why there is none to make.
 */
export const ReleasedDeskChoiceList: React.FC<ReleasedDeskChoiceListProps> = ({
  steps,
  deskName,
  vacatedDesk,
  overrides,
  onChange,
  isDisabled
}) => {
  const stepsFreeingADesk = steps.filter(
    (step) => vacatedDesk(step).kind !== "none"
  );
  if (stepsFreeingADesk.length === 0) return null;

  return (
    <div className="mt-3 rounded-brand border border-indigo-200 bg-white p-3">
      <h4 className={cn(classes.sectionHeading, "text-indigo-800")}>
        What happens to the desk being freed
      </h4>
      <ul className="mt-2 flex flex-col gap-3">
        {stepsFreeingADesk.map((step) => (
          <li key={step.workflowId}>
            <ReleasedDeskChoiceRow
              step={step}
              deskName={deskName}
              vacatedDesk={vacatedDesk(step)}
              value={overrides[step.workflowId] ?? step.releasedResourceAction}
              isOverridden={step.workflowId in overrides}
              onChange={(action) => onChange(step.workflowId, action)}
              isDisabled={isDisabled}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

interface ReleasedDeskChoiceRowProps {
  step: DeskAutomationStep;
  deskName: (resourceId: string | null) => string | null;
  vacatedDesk: VacatedDesk<IDedicatedResourceRef>;
  value: WorkplaceRequestReleasedResourceAction;
  isOverridden: boolean;
  onChange: (action: WorkplaceRequestReleasedResourceAction) => void;
  isDisabled?: boolean;
}

const ReleasedDeskChoiceRow: React.FC<ReleasedDeskChoiceRowProps> = ({
  step,
  deskName,
  vacatedDesk,
  value,
  isOverridden,
  onChange,
  isDisabled
}) => {
  // On a release the desk being freed is the one the rule names, so it can be
  // called by name. On an assignment the freed desk is whatever the person
  // holds today, which the request itself never carries; when the lookup on
  // the person found it, it is named, otherwise the desk they are moving to
  // at least locates the move.
  const named = deskName(step.resourceId);
  const label =
    step.action === "release"
      ? named
        ? `${named}, once the hold is released`
        : "The desk this releases"
      : assignmentLabel(named, vacatedDesk);

  return (
    <UnifiedSelectInput
      label={label}
      helperText={
        isOverridden
          ? "Applies to this request only — the request type keeps its own setting."
          : "Set on the request type. Change it here to do something different just this once."
      }
      items={OPTIONS}
      value={value}
      onChangeValue={(next) => onChange(readReleasedResourceAction(next))}
      disabled={isDisabled}
    />
  );
};

const assignmentLabel = (
  destinationName: string | null,
  vacatedDesk: VacatedDesk<IDedicatedResourceRef>
): string => {
  const leaving =
    vacatedDesk.kind === "known"
      ? `${vacatedDesk.desk.name}, the desk they leave`
      : "The desk they leave";
  return destinationName
    ? `${leaving} when they move to ${destinationName}`
    : `${leaving} behind`;
};
